import { AI_REQUEST_PRESETS } from './aiRequestPresets.js';
import { getAiInferenceProfile } from './aiInferenceProfiles.js';
import { getAiModelById, getAiModelByKey, getPreferredAiModel, getPreferredAiModelProfileDefault, inferAiProviderFromModel, normalizeAiModelKey, resolveAiModelId } from './aiModelSelectors.js';
const AI_REQUEST_PRESETS_BY_KEY = new Map(AI_REQUEST_PRESETS.map((preset) => [preset.key, preset]));
const OUTDATED_AI_MODEL_STATUSES = new Set(['legacy', 'deprecated', 'retired']);
function normalizeNonEmptyString(value) {
    const normalized = value?.trim() ?? '';
    return normalized ? normalized : null;
}
function resolveOverride(params) {
    if (params.overrideValue !== null && params.overrideValue !== undefined) {
        params.overridesApplied.push(String(params.fieldName));
        return params.overrideValue;
    }
    return params.presetValue;
}
function buildUnknownModelResult(params) {
    const explicitModel = params.explicitModel.trim();
    const providerPrefixedMatch = explicitModel.match(/^(anthropic|openai)\s*:\s*(.+)$/i);
    const modelId = providerPrefixedMatch?.[2]?.trim() || explicitModel;
    const normalizedModelKey = normalizeAiModelKey(explicitModel) ?? `${params.provider}:${modelId}`;
    params.warnings.push(`Model override "${explicitModel}" was not found in the shared catalog; capability validation and max-output clamping were skipped.`);
    return {
        catalogEntry: null,
        modelKey: normalizedModelKey,
        modelId,
        modelDisplayName: modelId
    };
}
function resolveProvider(params) {
    if (params.requestedProvider)
        return params.requestedProvider;
    if (params.explicitModel) {
        const inferredProvider = inferAiProviderFromModel(params.explicitModel);
        if (inferredProvider)
            return inferredProvider;
        const matchingCatalogModel = getAiModelByKey(params.explicitModel) ?? getAiModelById(params.explicitModel);
        if (matchingCatalogModel)
            return matchingCatalogModel.provider;
    }
    return params.preset.preferredProvider ?? 'openai';
}
function resolveInferenceProfileKey(params) {
    if (params.overrideValue !== null && params.overrideValue !== undefined) {
        params.overridesApplied.push('inferenceProfileKey');
        return params.overrideValue;
    }
    if (params.preset.inferenceProfileKey !== undefined)
        return params.preset.inferenceProfileKey;
    return getPreferredAiModelProfileDefault(params.provider, params.modelProfile).inferenceProfileKey;
}
function resolveInferenceProfile(inferenceProfileKey) {
    if (!inferenceProfileKey)
        return null;
    const inferenceProfile = getAiInferenceProfile(inferenceProfileKey);
    if (!inferenceProfile)
        throw new Error(`Unknown AI inference profile: ${inferenceProfileKey}`);
    return inferenceProfile;
}
function resolveCurrentCatalogModel(params) {
    const requestedModel = params.model;
    const replacementPath = [requestedModel.modelKey];
    const visitedModelKeys = new Set(replacementPath);
    let resolvedModel = requestedModel;
    while (OUTDATED_AI_MODEL_STATUSES.has(resolvedModel.status)) {
        const replacementModelKey = resolvedModel.recommendedReplacementModelKey;
        if (!replacementModelKey) {
            throw new Error(`Outdated model "${resolvedModel.modelKey}" (${resolvedModel.status}) has no recommended replacement in the shared catalog.`);
        }
        const replacementModel = getAiModelByKey(replacementModelKey);
        if (!replacementModel) {
            throw new Error(`Outdated model "${resolvedModel.modelKey}" recommends missing catalog model "${replacementModelKey}".`);
        }
        if (replacementModel.provider !== requestedModel.provider) {
            throw new Error(`Outdated model "${resolvedModel.modelKey}" recommends cross-provider replacement "${replacementModel.modelKey}".`);
        }
        if (visitedModelKeys.has(replacementModel.modelKey)) {
            throw new Error(`AI model replacement cycle detected: ${[...replacementPath, replacementModel.modelKey].join(' -> ')}.`);
        }
        replacementPath.push(replacementModel.modelKey);
        visitedModelKeys.add(replacementModel.modelKey);
        resolvedModel = replacementModel;
    }
    if (resolvedModel !== requestedModel) {
        params.warnings.push(`Outdated model "${requestedModel.modelKey}" (${requestedModel.status}) was automatically upgraded: ${replacementPath.join(' -> ')}.`);
    }
    return resolvedModel;
}
function resolveCatalogModel(params) {
    if (params.explicitModel) {
        const matchingCatalogModel = getAiModelByKey(params.explicitModel) ??
            getAiModelById(params.explicitModel, params.provider) ??
            getAiModelById(params.explicitModel);
        if (matchingCatalogModel) {
            if (matchingCatalogModel.provider !== params.provider) {
                throw new Error(`Explicit model "${params.explicitModel}" resolves to provider "${matchingCatalogModel.provider}", which does not match requested provider "${params.provider}".`);
            }
            const currentCatalogModel = resolveCurrentCatalogModel({
                model: matchingCatalogModel,
                warnings: params.warnings
            });
            return {
                catalogEntry: currentCatalogModel,
                modelKey: currentCatalogModel.modelKey,
                modelId: currentCatalogModel.modelId,
                modelDisplayName: currentCatalogModel.displayName
            };
        }
        return buildUnknownModelResult({
            explicitModel: params.explicitModel,
            provider: params.provider,
            warnings: params.warnings
        });
    }
    const preferredModel = getPreferredAiModel(params.provider, params.modelProfile);
    if (!preferredModel) {
        throw new Error(`No preferred model is configured for provider "${params.provider}" and profile "${params.modelProfile}". Supply an explicit model override or update aiModelProfiles.ts.`);
    }
    const currentPreferredModel = resolveCurrentCatalogModel({
        model: preferredModel,
        warnings: params.warnings
    });
    return {
        catalogEntry: currentPreferredModel,
        modelKey: currentPreferredModel.modelKey,
        modelId: currentPreferredModel.modelId,
        modelDisplayName: currentPreferredModel.displayName
    };
}
function maybeDisableStructuredOutputs(params) {
    if (!params.requested)
        return false;
    if (!params.catalogEntry)
        return true;
    if (params.catalogEntry.capabilities.supportsStructuredOutputs === false) {
        params.warnings.push(`Structured outputs were disabled because ${params.catalogEntry.modelKey} does not advertise structured-output support.`);
        return false;
    }
    return true;
}
function maybeDisableToolCalling(params) {
    if (!params.requested)
        return false;
    if (!params.catalogEntry)
        return true;
    if (params.catalogEntry.capabilities.supportsToolCalling === false) {
        params.warnings.push(`Tool calling was disabled because ${params.catalogEntry.modelKey} does not advertise tool-calling support.`);
        return false;
    }
    return true;
}
function maybeDisableBuiltInWebSearch(params) {
    if (!params.requested)
        return false;
    if (!params.catalogEntry)
        return true;
    if (params.catalogEntry.capabilities.supportsWebSearch === false) {
        params.warnings.push(`Built-in web search was disabled because ${params.catalogEntry.modelKey} does not advertise web-search support.`);
        return false;
    }
    return true;
}
function clampMaxOutputTokens(params) {
    const modelMaxOutputTokens = params.catalogEntry?.maxOutputTokens ?? null;
    if (params.requestedMaxOutputTokens === null || params.requestedMaxOutputTokens === undefined) {
        return modelMaxOutputTokens;
    }
    if (modelMaxOutputTokens !== null && params.requestedMaxOutputTokens > modelMaxOutputTokens) {
        params.warnings.push(`Requested max output tokens (${params.requestedMaxOutputTokens}) were capped to the model maximum (${modelMaxOutputTokens}).`);
        return modelMaxOutputTokens;
    }
    return params.requestedMaxOutputTokens;
}
function resolveReasoningEffort(params) {
    if (!params.requestedReasoningEffort)
        return null;
    if (!params.catalogEntry)
        return params.requestedReasoningEffort;
    if (params.catalogEntry.capabilities.supportsReasoningEffort === false) {
        params.warnings.push(`Reasoning effort was cleared because ${params.catalogEntry.modelKey} uses a different reasoning/thinking interface.`);
        return null;
    }
    const supportedLevels = params.catalogEntry.capabilities.reasoningEffortLevels;
    if (supportedLevels.length > 0 && !supportedLevels.includes(params.requestedReasoningEffort)) {
        params.warnings.push(`Reasoning effort "${params.requestedReasoningEffort}" was cleared because ${params.catalogEntry.modelKey} supports only: ${supportedLevels.join(', ')}.`);
        return null;
    }
    return params.requestedReasoningEffort;
}
function resolveRequestedReasoningEffort(params) {
    if (params.overrideValue !== null && params.overrideValue !== undefined) {
        params.overridesApplied.push('reasoningEffort');
        return params.overrideValue;
    }
    if (params.provider !== 'openai')
        return null;
    if (params.inferenceProfile)
        return params.inferenceProfile.reasoningEffort;
    return params.preset.reasoningEffort ?? null;
}
function resolveAnthropicThinkingBudgetTokens(params) {
    if (params.requestedBudgetTokens === null || params.requestedBudgetTokens === undefined)
        return null;
    if (params.provider !== 'anthropic') {
        params.warnings.push('Anthropic thinking budget tokens were cleared because the resolved provider is not Anthropic.');
        return null;
    }
    if (!params.catalogEntry)
        return params.requestedBudgetTokens;
    if (params.catalogEntry.capabilities.supportsExtendedThinking === false) {
        params.warnings.push(`Anthropic thinking budget tokens were cleared because ${params.catalogEntry.modelKey} does not advertise extended-thinking support.`);
        return null;
    }
    return params.requestedBudgetTokens;
}
function resolveRequestedAnthropicThinkingBudgetTokens(params) {
    if (params.overrideValue !== null && params.overrideValue !== undefined) {
        params.overridesApplied.push('anthropicThinkingBudgetTokens');
        return params.overrideValue;
    }
    if (params.provider !== 'anthropic')
        return null;
    if (params.inferenceProfile) {
        if (params.catalogEntry?.capabilities.supportsExtendedThinking === false &&
            params.catalogEntry.capabilities.supportsAdaptiveThinking === true) {
            return null;
        }
        return params.inferenceProfile.anthropicThinkingBudgetTokens;
    }
    return params.preset.anthropicThinkingBudgetTokens ?? null;
}
function resolveTemperature(params) {
    if (params.requestedTemperature === null || params.requestedTemperature === undefined)
        return null;
    if (params.provider === 'anthropic' && params.anthropicThinkingBudgetTokens !== null) {
        params.warnings.push('Temperature was cleared because Anthropic thinking budgets are incompatible with temperature overrides.');
        return null;
    }
    if (!params.catalogEntry)
        return params.requestedTemperature;
    const temperaturePolicy = params.catalogEntry.parameterPolicies?.temperature ?? 'supported';
    if (temperaturePolicy === 'unsupported') {
        params.warnings.push(`Temperature was cleared because ${params.catalogEntry.modelKey} does not support the temperature parameter.`);
        return null;
    }
    if (temperaturePolicy === 'requiresReasoningEffortNone' && params.reasoningEffort !== 'none') {
        params.warnings.push(`Temperature was cleared because ${params.catalogEntry.modelKey} only supports temperature when reasoning effort is set to "none".`);
        return null;
    }
    return params.requestedTemperature;
}
export function getAiRequestPreset(preset) {
    const matchingPreset = AI_REQUEST_PRESETS_BY_KEY.get(preset);
    if (!matchingPreset)
        throw new Error(`Unknown AI request preset: ${preset}`);
    return matchingPreset;
}
export function listAiRequestPresets(params) {
    const normalizedTags = params?.tags?.map((tag) => tag.trim().toLowerCase()).filter(Boolean) ?? [];
    return AI_REQUEST_PRESETS.filter((preset) => {
        if (params?.provider && preset.preferredProvider && preset.preferredProvider !== params.provider)
            return false;
        if (normalizedTags.length > 0) {
            const presetTags = new Set(preset.tags.map((tag) => tag.toLowerCase()));
            if (!normalizedTags.every((tag) => presetTags.has(tag)))
                return false;
        }
        return true;
    });
}
export function resolveAiRequestConfig(params) {
    const preset = getAiRequestPreset(params.preset);
    const warnings = [];
    const overridesApplied = [];
    const explicitModel = normalizeNonEmptyString(params.model);
    const provider = resolveProvider({
        requestedProvider: params.provider,
        explicitModel,
        preset
    });
    const modelProfile = resolveOverride({
        overrideValue: params.modelProfile,
        presetValue: preset.modelProfile,
        fieldName: 'modelProfile',
        overridesApplied
    });
    const inferenceProfileKey = resolveInferenceProfileKey({
        provider,
        modelProfile,
        preset,
        overrideValue: params.inferenceProfileKey,
        overridesApplied
    });
    const inferenceProfile = resolveInferenceProfile(inferenceProfileKey);
    const resolvedModel = resolveCatalogModel({
        provider,
        explicitModel,
        modelProfile,
        warnings
    });
    const requestedReasoningEffort = resolveRequestedReasoningEffort({
        provider,
        overrideValue: params.reasoningEffort,
        preset,
        inferenceProfile,
        overridesApplied
    });
    const requestedAnthropicThinkingBudgetTokens = resolveRequestedAnthropicThinkingBudgetTokens({
        provider,
        overrideValue: params.anthropicThinkingBudgetTokens,
        preset,
        inferenceProfile,
        catalogEntry: resolvedModel.catalogEntry,
        overridesApplied
    });
    const requestedTemperature = resolveOverride({
        overrideValue: params.temperature,
        presetValue: preset.temperature,
        fieldName: 'temperature',
        overridesApplied
    });
    const requestedMaxOutputTokens = resolveOverride({
        overrideValue: params.maxOutputTokens,
        presetValue: preset.maxOutputTokens,
        fieldName: 'maxOutputTokens',
        overridesApplied
    });
    const requestedStructuredOutputs = resolveOverride({
        overrideValue: params.useStructuredOutputs,
        presetValue: preset.useStructuredOutputs,
        fieldName: 'useStructuredOutputs',
        overridesApplied
    });
    const requestedToolCalling = resolveOverride({
        overrideValue: params.useToolCalling,
        presetValue: preset.useToolCalling,
        fieldName: 'useToolCalling',
        overridesApplied
    });
    const requestedParallelToolCalls = resolveOverride({
        overrideValue: params.parallelToolCalls,
        presetValue: preset.parallelToolCalls,
        fieldName: 'parallelToolCalls',
        overridesApplied
    });
    const requestedToolChoice = resolveOverride({
        overrideValue: params.toolChoice,
        presetValue: preset.toolChoice,
        fieldName: 'toolChoice',
        overridesApplied
    });
    const requestedBuiltInWebSearch = resolveOverride({
        overrideValue: params.useBuiltInWebSearch,
        presetValue: preset.useBuiltInWebSearch,
        fieldName: 'useBuiltInWebSearch',
        overridesApplied
    });
    const requestedBuiltInWebSearchContextSize = resolveOverride({
        overrideValue: params.builtInWebSearchContextSize,
        presetValue: preset.builtInWebSearchContextSize,
        fieldName: 'builtInWebSearchContextSize',
        overridesApplied
    });
    const useStructuredOutputs = maybeDisableStructuredOutputs({
        requested: requestedStructuredOutputs,
        catalogEntry: resolvedModel.catalogEntry,
        warnings
    });
    const useToolCalling = maybeDisableToolCalling({
        requested: requestedToolCalling,
        catalogEntry: resolvedModel.catalogEntry,
        warnings
    });
    const useBuiltInWebSearch = maybeDisableBuiltInWebSearch({
        requested: requestedBuiltInWebSearch,
        catalogEntry: resolvedModel.catalogEntry,
        warnings
    });
    const reasoningEffort = resolveReasoningEffort({
        requestedReasoningEffort,
        catalogEntry: resolvedModel.catalogEntry,
        warnings
    });
    const anthropicThinkingBudgetTokens = resolveAnthropicThinkingBudgetTokens({
        provider,
        requestedBudgetTokens: requestedAnthropicThinkingBudgetTokens,
        catalogEntry: resolvedModel.catalogEntry,
        warnings
    });
    const temperature = resolveTemperature({
        requestedTemperature,
        provider,
        catalogEntry: resolvedModel.catalogEntry,
        reasoningEffort,
        anthropicThinkingBudgetTokens,
        warnings
    });
    const maxOutputTokens = clampMaxOutputTokens({
        requestedMaxOutputTokens,
        catalogEntry: resolvedModel.catalogEntry,
        warnings
    });
    const parallelToolCalls = useToolCalling ? requestedParallelToolCalls : null;
    const toolChoice = useToolCalling ? requestedToolChoice : 'none';
    const builtInWebSearchContextSize = useBuiltInWebSearch ? requestedBuiltInWebSearchContextSize : null;
    return {
        preset: preset.key,
        presetDescription: preset.description,
        provider,
        modelProfile,
        inferenceProfileKey,
        catalogEntry: resolvedModel.catalogEntry,
        modelKey: resolvedModel.catalogEntry?.modelKey ?? resolvedModel.modelKey,
        modelId: resolvedModel.catalogEntry
            ? resolveAiModelId(resolvedModel.catalogEntry, params.preferSnapshot ?? false)
            : resolvedModel.modelId,
        modelDisplayName: resolvedModel.modelDisplayName,
        requestedMaxOutputTokens,
        modelMaxOutputTokens: resolvedModel.catalogEntry?.maxOutputTokens ?? null,
        maxOutputTokens,
        reasoningEffort,
        anthropicThinkingBudgetTokens,
        temperature,
        useStructuredOutputs,
        useToolCalling,
        parallelToolCalls,
        toolChoice,
        useBuiltInWebSearch,
        builtInWebSearchContextSize,
        warnings,
        overridesApplied
    };
}
//# sourceMappingURL=aiRequestSelectors.js.map